import type { QueryClient } from "@tanstack/react-query";
import type { Readiness } from "./page-lifecycle";

// Observe state only. Never forward keys, data, error objects or request options.
// Optional widgets may set meta.faroOptional=true. Cached data/background refresh
// is not an initial-load dependency, including polling after the first result.
export const observeQueryReadiness = (
  client: QueryClient,
  signal: (key: object, state?: Readiness) => void
) => {
  const active = new Set<object>();

  const sync = () => {
    const seen = new Set<object>();

    for (const query of client.getQueryCache().getAll()) {
      const state = query.state;
      if (
        !query.isActive() ||
        query.meta?.faroOptional === true ||
        state.data !== undefined
      ) {
        continue;
      }

      const readiness = state.fetchStatus !== "idle" ? "loading" : undefined;
      if (readiness) {
        seen.add(query);
        signal(query, readiness);
      }
    }

    active.forEach((key) => {
      if (!seen.has(key)) {
        signal(key);
      }
    });

    active.clear();
    seen.forEach((key) => active.add(key));
  };

  sync();
  const unsubscribe = client.getQueryCache().subscribe(sync);

  return () => {
    unsubscribe();
    active.forEach((key) => signal(key));
  };
};
